import { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { db } from '../db/shiksha-db';
import { useAuth } from '../hooks/useAuth';
import { Loader2 } from 'lucide-react';

/**
 * PrivacyGate ensures that the Data Fiduciary (Admin) has explicitly
 * signed the DPDP Consent Agreement before accessing sensitive PII routes.
 */
export function PrivacyGate({ children }) {
  const [hasConsent, setHasConsent] = useState(null);
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    async function checkConsent() {
      if (!user) {
        // PrivacyGate is always rendered inside ProtectedRoute, which
        // guarantees `user` will eventually resolve. During the transient
        // window where getIdToken(true) is refreshing the token, user may
        // briefly be null. Stay in loading state (hasConsent = null) rather
        // than prematurely rejecting, which caused a redirect ping-pong.
        return;
      }
      try {
        const consent = await db.dpdpConsent
          .where('adminEmail')
          .equals(user.email)
          .first();
        
        if (consent && consent.status === 'agreed') {
          setHasConsent(true);
        } else {
          setHasConsent(false);
        }
      } catch (err) {
        console.error("DPDP Consent Check Failed:", err);
        setHasConsent(false);
      }
    }
    checkConsent();
  }, [user]);

  if (hasConsent === null) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!hasConsent) {
    // Redirect to consent page, forcing them to sign before proceeding
    return <Navigate to="/admin/consent" state={{ from: location, message: 'DPDP Agreement Required' }} replace />;
  }

  return children;
}
